import { FormikProps, FormikErrors } from 'formik'
import isEqual from 'lodash/isEqual'
import isEmpty from 'lodash/isEmpty'
import { User, TagRequest } from '@graphics/gfx-common/dist/types'
import {
  IPackInput,
  ISelectInput,
  PackType,
  PreviewOptions,
  IEditionMetadataInput,
  IPromoDestination,
  IPreviewImages,
  EditionLanguage,
  PackLanguage,
  EditionLanguageInput,
  IEditionMetadata,
  Embed,
} from '../types/pack'
import { IEditor } from '../types/editorsPerGroup'
import { IUserGroupPreferences } from '../types/userPreferences'
import { IOption } from '../types/common'
import { trimExtraSpaces, isEmptyObject } from './common'
import {
  typeaheadToFormAdapter,
  topicsTypeaheadToFormAdapter,
  formToTypeaheadAdapter,
} from './typeahead'
import { updatePublishOptions } from './pack'

// Overall pack

export const transformUserToForm = (user: User): ISelectInput => ({
  value: user.id,
  label: user.name,
  ...user,
})

export const transformTagToForm = ({ id, text }: TagRequest): ISelectInput => ({
  id,
  label: text,
  value: text,
})

export const transformPackStateToInput = ({
  isLoading,
  location,
  topicCodes,
  contacts,
  tags,
  slugline,
  title,
  description,
  byline,
  editions = [],
  language,
  ...state
}: PackType): IPackInput => ({
  ...state,
  slugline: slugline ?? '',
  title: title ?? '',
  description: description ?? '',
  byline: byline ?? '',
  location: location && typeaheadToFormAdapter(location),
  language: language ?? '',
  topicCodes: topicCodes.filter(Boolean).map(topicsTypeaheadToFormAdapter),
  contacts: contacts.map(transformUserToForm),
  tags: tags.map(transformTagToForm),
  useGraphicPreview: PreviewOptions.useGraphicPreview,
  editions: editions.map(transformEditonStateToInput),
})

const pickNewFields = ({
  newFileId: fileId,
  newTmpPreview,
  ...rest
}: IPreviewImages): IPreviewImages => (fileId ? { fileId } : rest)

const transformPreviewImages = ({
  useGraphicPreview,
  previewImages,
}: IPackInput | IEditionMetadataInput): IPreviewImages | null =>
  useGraphicPreview === PreviewOptions.useUploadedPreview
    ? previewImages && pickNewFields(previewImages)
    : previewImages

const transformEmbedInputToState = (embed: Embed): Embed =>
  embed && (embed.declaration || embed.dependencies) ? embed : undefined

export const transformPackInputToState = ({
  useGraphicPreview,
  ...pack
}: IPackInput): PackType => ({
  ...pack,
  contacts: pack.contacts.map(({ value: id, label: name }) => ({
    id,
    name,
  })),
  tags: pack.tags.map(({ id, value }) => ({
    id,
    text: value,
  })),
  topicCodes: pack.topicCodes.map(formToTypeaheadAdapter),
  previewImages: transformPreviewImages({ useGraphicPreview, ...pack }),
  editions: pack.editions.map((edition) => ({
    ...edition,
    language: correctLanguage(pack.language, edition.language),
    dataStreamGraphic: {
      dataStreamURI: edition.dataStreamURI,
    },
    useGraphicPreview:
      edition.useGraphicPreview === PreviewOptions.useGraphicPreview,
    previewImages: transformPreviewImages(edition),
    publishing: updatePublishOptions(edition),
    embed: transformEmbedInputToState(edition.embed),
  })),
})

export const editorsStateToInputMapper = ({
  id = '',
  firstName = '',
  middleName = '',
  lastName = '',
}: IEditor): ISelectInput =>
  transformUserToForm({
    id,
    name: trimExtraSpaces(`${firstName} ${middleName} ${lastName}`),
  })

export const arePackPublishOptionsChanged = (
  editions: IEditionMetadataInput[]
): boolean =>
  editions.some(({ publishingLocations, publishing }) => {
    const editionPublishOptionsHaveBeenChanged =
      publishing.locations &&
      publishing.locations.some((location) =>
        location.published
          ? !publishingLocations.includes(location.name)
          : publishingLocations.includes(location.name)
      )
    return !!editionPublishOptionsHaveBeenChanged
  })

export const arePackFormValuesEmpty = (pack: IPackInput): boolean =>
  // Skip title as it may be prepopulated when creating a pack from DataStream Chart
  !pack.description &&
  !pack.byline &&
  !pack.language &&
  !pack.slugline &&
  isEmpty(pack.location) &&
  isEmpty(pack.topicCodes) &&
  // Skip contacts as they may be prepopulated when creating a pack from file
  isEmpty(pack.tags)

export const createPackFormFieldsSetter = ({
  initialValues,
  setFieldValue,
}: Pick<FormikProps<IPackInput>, 'initialValues' | 'setFieldValue'>) => (
  defaults: IUserGroupPreferences | undefined,
  user: User
) => {
  if (!initialValues.title && defaults?.title) {
    setFieldValue('title', defaults.title)
  }
  if (defaults?.description) {
    setFieldValue('description', defaults.description)
  }
  if (defaults?.byline) {
    setFieldValue('byline', defaults.byline)
  }
  if (defaults?.language) {
    setFieldValue('language', defaults.language)
  }
  if (defaults?.slugline) {
    setFieldValue('slugline', defaults.slugline)
  }
  if (defaults?.location) {
    setFieldValue('location', defaults.location)
  }
  if (defaults?.topiccodes) {
    setFieldValue('topicCodes', defaults.topiccodes)
  }
  if (isEmpty(initialValues.contacts)) {
    setFieldValue('contacts', defaults?.contacts || [transformUserToForm(user)])
  }
  if (defaults?.tags) {
    setFieldValue('tags', defaults.tags)
  }
}

export const areCaseInsensitiveEqual = (first: string) => (second: string) =>
  trimExtraSpaces(first.toLowerCase()) === trimExtraSpaces(second.toLowerCase())

// Editions

export const transformEditonStateToInput = (
  edition: IEditionMetadata
): IEditionMetadataInput => ({
  ...edition,
  language:
    edition.language === null
      ? EditionLanguageInput.usePackValue
      : edition.language,
  useGraphicPreview: edition.useGraphicPreview
    ? PreviewOptions.useGraphicPreview
    : PreviewOptions.useFilePreview,
  title: edition.title ?? '',
  description: edition.description ?? '',
  slugline: edition.slugline ?? '',
  dataStreamURI:
    (edition.dataStreamGraphic && edition.dataStreamGraphic.dataStreamURI) ??
    '',
  isLynxSearchable: edition.publishing && edition.publishing.promote,
  publishingLocations:
    edition.publishing && edition.publishing.locations
      ? edition.publishing.locations
          .filter(({ publish }) => publish)
          .map(({ name }) => name)
      : [],
  embed: edition.embed || edition.editionSubType === 'Interactive'
    ? {
        ...edition.embed,
        declaration: edition.embed?.declaration ?? '',
        dependencies: edition.embed?.dependencies ?? '',
      }
    : undefined,
})

export const editionFieldOfInterest = [
  'title',
  'description',
  'language',
  'slugline',
  'useGraphicPreview',
  'dataStreamURI',
  'editStatus',
  'releaseNote',
  'embed',
]

export const createLanguageTransform = (replacement: EditionLanguage) => (
  value: EditionLanguage
): EditionLanguage => (!isLanguageSelected(value) ? replacement : value)

export const getObjectForTracking = (
  obj: object,
  fieldNames: string[],
  transform?: object
): object =>
  fieldNames.reduce(
    (acc, fieldName) => ({
      ...acc,
      [fieldName]:
        transform && transform[fieldName]
          ? transform[fieldName](obj[fieldName]) // ex: language
          : obj[fieldName],
    }),
    {}
  )

export const objectsAreNotEqual = (
  obj1: object,
  obj2: object,
  useLodashIsEqual = false
): boolean =>
  useLodashIsEqual
    ? !isEqual(obj1, obj2)
    : JSON.stringify(obj1) !== JSON.stringify(obj2)

const getEditionTrackingObject = (values: object, transform?: object) =>
  getObjectForTracking(values, editionFieldOfInterest, transform)

export const isEditionModified = (
  initValues: object,
  values: object,
  transform?: any,
  useLodashIsEqual = false
): boolean =>
  objectsAreNotEqual(
    getEditionTrackingObject(initValues, transform),
    getEditionTrackingObject(values, transform),
    useLodashIsEqual
  )

export const isEditionPublished = ({
  publishing: { published },
}: IEditionMetadataInput): boolean => published

export const isEditionChanged = ({
  publishing: { changed },
}: IEditionMetadataInput): boolean => changed

export const areSomeEditionsPublished = (
  editions: IEditionMetadataInput[]
): boolean => editions.some(isEditionPublished)

export const areSomeEditionsChanged = (
  editions: IEditionMetadataInput[]
): boolean => editions.some(isEditionChanged)

export const arePackPromotionDestinationsChanged = (
  editions: IEditionMetadataInput[]
): boolean =>
  editions.some(({ publishing, isLynxSearchable }) => {
    const lynxEvents = publishing.promoDestinations.find(
      ({ name }) => name === 'LynxEvents'
    ) as IPromoDestination
    return (
      lynxEvents &&
      (isLynxSearchable ? !lynxEvents.promoted : lynxEvents.promoted)
    )
  })

export const isLanguageSelected = (value: EditionLanguage): boolean =>
  !!value && value !== EditionLanguageInput.usePackValue

export const getEditionLanguage = (
  packLanguage: PackLanguage,
  editionLanguage: EditionLanguage
): PackLanguage | EditionLanguage =>
  isLanguageSelected(editionLanguage) ? editionLanguage : packLanguage

export const getEditionLanguageInputValue = (
  packLanguage: PackLanguage,
  editionLanguage: EditionLanguage
): EditionLanguage =>
  checkIsLanguageInherit(packLanguage, editionLanguage)
    ? EditionLanguageInput.usePackValue
    : editionLanguage

export const checkIsLanguageInherit = (
  packLanguage: PackLanguage,
  editionLanguage: EditionLanguage
): boolean =>
  !isLanguageSelected(editionLanguage) || packLanguage === editionLanguage

export const correctLanguage = (
  packLanguage,
  editionLanguage
): EditionLanguage =>
  checkIsLanguageInherit(packLanguage, editionLanguage) ? null : editionLanguage

// Errors

export const getPackErrors = ({
  editions = [],
  ...overallPack
}: FormikErrors<IPackInput>): {
  overallPack: boolean
  editions: string[]
} => ({
  overallPack: !isEmptyObject(overallPack),
  editions: editions
    .map((editionErrors, index: number) => !!editionErrors && `${index}`)
    .filter(Boolean) as string[],
})

const genericErrorMessages = {
  METADATA: 'Metadata is incomplete.',
  REVISION: 'Revision Type/Note is missing.',
  PREVIEW: 'Preview file is missing.',
  EDITION: 'There is a problem in an edition.',
}

export const getGenericEditionsErrors = ({
  editions: editionsErrors = [],
}: FormikErrors<IPackInput>): Array<string | undefined> =>
  editionsErrors.map(
    (err) =>
    err &&
      [
        (err.title || err.description || err.slugline || err.embed) &&
          genericErrorMessages.METADATA,
        (err.editStatus || err.releaseNote) && genericErrorMessages.REVISION,
        err.useGraphicPreview && genericErrorMessages.PREVIEW,
      ]
        .filter(Boolean)
        .join('\n')
  )

export const getGenericPackError = ({
  editions: editionsErrors,
  useGraphicPreview,
  ...packErrors
}: FormikErrors<IPackInput>): string =>
  [
    !isEmptyObject(packErrors) && genericErrorMessages.METADATA,
    useGraphicPreview && genericErrorMessages.PREVIEW,
    editionsErrors && genericErrorMessages.EDITION,
  ]
    .filter(Boolean)
    .join('\n')

// Miscellaneous

export const validateNewTag = (
  inputValue: string,
  selectValue: IOption[],
  selectOptions: IOption[]
): boolean =>
  !(
    inputValue.trim().length === 0 ||
    [...selectValue, ...selectOptions].some(({ value }) =>
      areCaseInsensitiveEqual(value)(inputValue)
    )
  )
